import { PlaceSummary } from "@/types/place"
import {
  GooglePlacePrediction,
  PlaceTypeAheadSearchResult,
} from "@/types/place/place-search-input"
import { cn } from "cn"

import { formatPlaceLocale } from "@/lib/format-utils"
import {
  getTypeaheadGooglePlaces,
  getTypeaheadPlaces,
  getTypeaheadSearchStringResults,
  hasTypeaheadPlaceOrGoogleResults,
  highlightMatch,
} from "@/lib/place-typeahead-utils"
import { convertDistance } from "@/lib/utils/geo"
import IconSearch from "@/components/icons/search"
import { EntityIcon } from "./entity-icon"
import { Loading } from "./ui/loading"
import { Photo } from "./ui/photo"

export function PlaceTypeaheadResults({
  typeaheadResults,
  query,
  originalSearchTerm,
  highlightedIndex,
  onHighlightChange,
  onSearchStringClick,
  onPlaceClick,
  onGooglePlaceClick,
  resolvingGooglePlaceId,
  showDistance = false,
  unitSystem = "metric",
  minChars = 3,
  className,
  onMouseLeave,
}: {
  typeaheadResults: PlaceTypeAheadSearchResult | null
  query: string
  originalSearchTerm: string
  highlightedIndex: number
  onHighlightChange: (index: number) => void
  onSearchStringClick: (searchTerm: string) => void
  onPlaceClick: (place: PlaceSummary) => void
  onGooglePlaceClick: (googlePlace: GooglePlacePrediction) => void
  resolvingGooglePlaceId: string | null
  showDistance?: boolean
  unitSystem?: "metric" | "imperial"
  minChars?: number
  className?: string
  onMouseLeave?: () => void
}) {
  const typeaheadStringResults = getTypeaheadSearchStringResults({
    query,
    originalSearchTerm,
    highlightedIndex,
    results: typeaheadResults,
  })
  const hasPlaceOrGoogleResults = hasTypeaheadPlaceOrGoogleResults(
    typeaheadResults,
    minChars,
    query.length
  )
  const highlightQuery = highlightedIndex > -1 ? originalSearchTerm : query

  const formatPlaceDistance = (place: PlaceSummary) => {
    if (!place.distance_km) return null
    const distance = convertDistance(place.distance_km * 1000, unitSystem)
    const unit = unitSystem === "imperial" ? "mi" : "km"
    return (
      <span>
        {distance.toFixed(1)} {unit}
      </span>
    )
  }

  return (
    <ul
      role="listbox"
      aria-label="Place suggestions"
      className={cn("-mx-1.5", className)}
      onMouseLeave={onMouseLeave}
    >
      {typeaheadStringResults.map((searchStr, index) => (
        <li
          role="option"
          key={"search-term-" + index}
          aria-selected={highlightedIndex === index}
          className={cn(
            "flex cursor-pointer items-center gap-2.5 rounded-xl p-1.5 font-semibold",
            highlightedIndex === index && "bg-foreground/5"
          )}
          onMouseEnter={() => onHighlightChange(index)}
          onMouseDown={() => {
            onSearchStringClick(searchStr)
          }}
        >
          <span className="flex size-9 items-center justify-center">
            <IconSearch size={24} />
          </span>
          <span className="no-translate">{searchStr}</span>
        </li>
      ))}

      {hasPlaceOrGoogleResults &&
        getTypeaheadPlaces(typeaheadResults).map((place, index) => {
          const actualIndex = index + typeaheadStringResults.length
          const isActive = actualIndex === highlightedIndex
          return (
            <li
              key={place.entity_ref || `suggestion-${index}`}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => onHighlightChange(actualIndex)}
              onMouseDown={() => onPlaceClick(place)}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-xl p-1.5",
                isActive && "bg-foreground/5"
              )}
            >
              <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-foreground/5">
                {place.photos?.length ? (
                  <Photo
                    src={place.photos[0]}
                    className="absolute inset-0 size-full"
                    width="sm"
                    alt=""
                  />
                ) : (
                  <EntityIcon type={place.type} size={18} />
                )}
              </div>
              <div className="no-translate min-w-0">
                <div className="truncate">
                  {highlightMatch(place.name, highlightQuery)}
                </div>
                <div
                  className={cn(
                    "text-2xs text-muted",
                    showDistance && "flex flex-wrap gap-1"
                  )}
                >
                  <span>{formatPlaceLocale(place)}</span>
                  {showDistance && (
                    <>
                      <span className="last:hidden">·</span>
                      {formatPlaceDistance(place)}
                    </>
                  )}
                </div>
              </div>
            </li>
          )
        })}

      {hasPlaceOrGoogleResults &&
        getTypeaheadGooglePlaces(typeaheadResults).map((googlePlace, index) => {
          const actualIndex =
            index +
            typeaheadStringResults.length +
            getTypeaheadPlaces(typeaheadResults).length
          const isActive = actualIndex === highlightedIndex
          const displayName = googlePlace.formatted_text

          return (
            <li
              key={googlePlace.place_id}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => onHighlightChange(actualIndex)}
              onMouseDown={(e) => {
                e.preventDefault()
                onGooglePlaceClick(googlePlace as GooglePlacePrediction)
              }}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-xl p-1.5",
                isActive && "bg-foreground/5"
              )}
            >
              <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-foreground/5">
                {resolvingGooglePlaceId === googlePlace.place_id ? (
                  <Loading size={4} />
                ) : (
                  <EntityIcon type={googlePlace.entity_type} size={18} />
                )}
              </div>
              <div className="no-translate min-w-0">
                <div className="truncate">
                  {highlightMatch(displayName, highlightQuery)}
                </div>
                <div className="text-2xs text-muted">
                  {googlePlace.secondary_text}
                </div>
              </div>
            </li>
          )
        })}
    </ul>
  )
}
